import api from "./api";
import eventBus from "@/services/eventBus";

// Megosztott állapot + adat-logika a főkönyvi riport-definíció LISTA (RiportDefinitionView)
// és a LAPALAPÚ szerkesztő (RiportDefinitionEditView) között. Singleton, mint a TransactionStore.
// A kiválasztások és a betöltött listák itt élnek, így túlélik a navigációt (lista ↔ szerkesztő-lap),
// és a szerkesztő ugyanazt a blankModel/persist/reload logikát használja, mint régen a dialógus.
export class RiportStore {
  types: any[] = [];
  years: any[] = [];
  rowTypes: any[] = [];

  selectedType: number | null = null;
  heads: any[] = [];
  selectedHeadId: number | null = null;
  selMainGroupId: number | null = null;
  selGroupId: number | null = null;
  selRowId: number | null = null;
  selRowItemId: number | null = null;
  selMainItemId: number | null = null;
  onlyLive = true;

  mainGroups: any[] = [];
  groups: any[] = [];
  rows: any[] = [];
  rowItems: any[] = [];
  mainItems: any[] = [];
  allGroups: any[] = [];
  allRows: any[] = [];
  // A riport évéhez tartozó dimenziók (a sortétel dimenzió-maszk LookupComboBox-aihoz).
  dimensions: any[] = [];
  // A riport évéhez tartozó aktív főkönyvi számok (a sortétel "Főkönyvi szám maszk" LookupComboBox-ához).
  accounts: any[] = [];

  // A szerkesztő-lapnak átadott rekord (módosításnál); create-nél null.
  editItem: any = null;
  busy = false;

  // A fel/le mozgatás piszkozat-állapota szintenként: true, ha van nem mentett sorrend-változás.
  // A nyilak csak a kliensoldali sorrendet állítják; a DB-be a saveOrder ír, a cancelOrder visszatölt.
  orderDirty: { maingroup: boolean; group: boolean; row: boolean } = { maingroup: false, group: false, row: false };

  // Az "elem" (imbokrepmainitem) előjele – 2 állapotú (K2D d_g_bokepmainitem: +/-).
  signItems = [
    { text: "+ (hozzáad)", value: "+" },
    { text: "- (kivon)", value: "-" },
  ];

  // A fő­csoport/csoport/sor "Előjel az összegzésben" – 3 állapotú (K2D d_g_imbokrep* gridek: +/-/0).
  // 0 = nem szerepel a totálban. Alapérték "+".
  nodeSignItems = [
    { text: "+ (hozzáad)", value: "+" },
    { text: "- (kivon)", value: "-" },
    { text: "0 (nem szerepel)", value: "0" },
  ];

  async loadLookups(): Promise<void> {
    try {
      this.types = await api.GetRiportTypes();
      this.years = await api.GetGeneralLedgerYears();
      if (this.types.length > 0 && !this.selectedType) {
        this.selectedType = this.types[0].id;
      }
    } catch (e) {
      eventBus.$emit("error", "A törzsadatok betöltése nem sikerült!");
    }
  }

  async loadHeads(): Promise<void> {
    if (!this.selectedType) { this.selectedHeadId = null; this.heads = []; return; }
    try {
      const list = ((await api.GetRiportHeadsByType(this.selectedType, !this.onlyLive)) || [])
        .map((h: any) => ({ ...h, deleting: false, selected: false }));
      // A korábbi kiválasztás megtartása, ha a fej még a listában van ("Csak élők" váltás / mentés utáni reload); különben töröljük.
      if (this.selectedHeadId && !list.some((h: any) => h.id === this.selectedHeadId)) {
        this.selectedHeadId = null;
      }
      list.forEach((h: any) => { h.selected = h.id === this.selectedHeadId; });
      this.heads = list;
    } catch (e) {
      this.heads = [];
    }
  }

  // Fej kiválasztása a táblából (a v-select helyett): beállítja a selectedHeadId-t (a View watchere futtatja az onHeadChanged-et) és a sor-kiemelést.
  selectHead(id: number): void {
    this.selectedHeadId = id;
    this.heads.forEach((h: any) => { h.selected = h.id === id; });
  }

  async loadRowTypes(): Promise<void> {
    // A sortípusok riporttípushoz kötöttek (csbokreprowtype.reptype) – a kiválasztott típusra szűrünk.
    this.rowTypes = [];
    if (!this.selectedType) return;
    try {
      this.rowTypes = (await api.GetRiportRowTypes(this.selectedType)) || [];
    } catch (e) {
      this.rowTypes = [];
    }
  }

  async loadMainGroups(): Promise<void> {
    this.orderDirty.maingroup = false; // friss DB-lista → nincs függőben lévő sorrend-piszkozat
    if (!this.selectedHeadId) { this.mainGroups = []; return; }
    this.mainGroups = ((await api.GetRiportMainGroups(this.selectedHeadId, !this.onlyLive)) || []).map((x: any) => ({ ...x, deleting: false, selected: x.id === this.selMainGroupId }));
  }
  async loadGroups(): Promise<void> {
    this.orderDirty.group = false;
    if (!this.selMainGroupId) { this.groups = []; return; }
    this.groups = ((await api.GetRiportGroups(this.selMainGroupId, !this.onlyLive)) || []).map((x: any) => ({ ...x, deleting: false, selected: x.id === this.selGroupId }));
  }
  async loadRows(): Promise<void> {
    this.orderDirty.row = false;
    if (!this.selGroupId) { this.rows = []; return; }
    this.rows = ((await api.GetRiportRows(this.selGroupId, !this.onlyLive)) || []).map((x: any) => ({ ...x, deleting: false, selected: x.id === this.selRowId }));
  }
  async loadRowItems(): Promise<void> {
    if (!this.selRowId) { this.rowItems = []; return; }
    this.rowItems = ((await api.GetRiportRowItemsByRow(this.selRowId, !this.onlyLive)) || []).map((x: any) => ({ ...x, deleting: false, selected: x.id === this.selRowItemId }));
  }
  async loadMainItems(): Promise<void> {
    if (!this.selMainGroupId) { this.mainItems = []; return; }
    this.mainItems = ((await api.GetRiportMainItems(this.selMainGroupId)) || []).map((x: any) => ({ ...x, deleting: false, selected: x.id === this.selMainItemId }));
  }
  // Az "elem"-szerkesztő legördülőihez: a fej összes csoportja/sora. A főcsoportokat a mainGroups adja.
  async loadHeadLookups(): Promise<void> {
    if (!this.selectedHeadId) { this.allGroups = []; this.allRows = []; return; }
    try {
      this.allGroups = (await api.GetRiportGroupsByHead(this.selectedHeadId)) || [];
      this.allRows = (await api.GetRiportRowsByHead(this.selectedHeadId)) || [];
    } catch (e) {
      this.allGroups = []; this.allRows = [];
    }
  }

  // Watcher-logika: a plain store nem tud önmagára figyelni, ezért a View watcherei hívják.
  async onTypeChanged(): Promise<void> {
    await this.loadHeads();
    await this.loadRowTypes();
  }
  async onHeadChanged(): Promise<void> {
    this.selMainGroupId = null;
    this.selGroupId = null;
    this.selRowId = null;
    this.selRowItemId = null;
    this.selMainItemId = null;
    this.groups = [];
    this.rows = [];
    this.rowItems = [];
    this.mainItems = [];
    this.dimensions = []; // a fej (és vele az év) válthat → a dimenziók a sortétel-szerkesztőben újratöltődnek
    this.accounts = []; // ugyanígy a főkönyvi számok is évhez kötöttek → újratöltjük a sortétel-szerkesztőben
    await this.loadMainGroups();
    await this.loadHeadLookups();
  }

  // A kiválasztott riport fej évéhez tartozó dimenziók (a tranzakciós dimenzió-végpontról). Lazy: a sortétel-szerkesztő hívja.
  async loadDimensions(): Promise<void> {
    const head = this.heads.find((h: any) => h.id === this.selectedHeadId);
    let yearNum: number | null = null;
    if (head) {
      const yObj = this.years.find((y: any) => y.id === head.year);
      yearNum = yObj ? yObj.year : (head.year >= 1900 ? head.year : null);
    }
    if (!yearNum) yearNum = new Date().getFullYear(); // nincs megadott év a fejnél → aktuális év
    try {
      this.dimensions = (await api.GetReportDimensions(yearNum)) || [];
    } catch (e) {
      this.dimensions = [];
    }
  }

  // A kiválasztott riport fej évéhez tartozó aktív főkönyvi számok (mmbokaccno) – a sortétel accno-maszk választójához.
  // Ugyanaz az év-számítás, mint a loadDimensions-nél (a fej year mezője év-id vagy közvetlen évszám lehet).
  async loadAccounts(): Promise<void> {
    const head = this.heads.find((h: any) => h.id === this.selectedHeadId);
    let yearNum: number | null = null;
    if (head) {
      const yObj = this.years.find((y: any) => y.id === head.year);
      yearNum = yObj ? yObj.year : (head.year >= 1900 ? head.year : null);
    }
    if (!yearNum) yearNum = new Date().getFullYear(); // nincs megadott év a fejnél → aktuális év
    try {
      let list = (await api.GetInvoiceMirrorsByYear(yearNum)) || [];
      // Ha a riport évéhez nincs beállított főkönyvi szám (mmbokaccno), essünk vissza a legutóbbi évre:
      // az accno itt csak MASZK-segéd, a számok évtől lényegében függetlenül hasznosak a maszk összeállításához.
      if (!list.length && this.years.length) {
        const newest = [...this.years].sort((a: any, b: any) => (b.year || 0) - (a.year || 0))[0];
        if (newest && newest.year && newest.year !== yearNum) {
          list = (await api.GetInvoiceMirrorsByYear(newest.year)) || [];
        }
      }
      this.accounts = list;
    } catch (e) {
      this.accounts = [];
    }
  }
  async onOnlyLiveChanged(): Promise<void> {
    await this.loadHeads(); // a fej-tábla is szűrjön a "Csak élők"-re (a kiválasztás megmarad, ha a fej élő)
    await this.loadMainGroups();
    await this.loadGroups();
    await this.loadRows();
    await this.loadRowItems();
  }

  selectMainGroup(id: number): void {
    this.selMainGroupId = id;
    this.selGroupId = null;
    this.selRowId = null;
    this.selRowItemId = null;
    this.selMainItemId = null;
    this.rows = [];
    this.rowItems = [];
    this.mainItems = [];
    this.mainGroups.forEach((x: any) => (x.selected = x.id === id));
    this.loadGroups();
    this.loadMainItems();
  }
  selectGroup(id: number): void {
    this.selGroupId = id;
    this.selRowId = null;
    this.selRowItemId = null;
    this.rowItems = [];
    this.groups.forEach((x: any) => (x.selected = x.id === id));
    this.loadRows();
  }
  selectRow(id: number): void {
    this.selRowId = id;
    this.selRowItemId = null;
    this.rows.forEach((x: any) => (x.selected = x.id === id));
    this.loadRowItems();
  }
  // A "levél" gridek (sortétel, elem) sor-kijelölése – csak vizuális kiemelés.
  selectRowItem(id: number): void {
    this.selRowItemId = id;
    this.rowItems.forEach((x: any) => (x.selected = x.id === id));
  }
  selectMainItem(id: number): void {
    this.selMainItemId = id;
    this.mainItems.forEach((x: any) => (x.selected = x.id === id));
  }

  nextOrder(arr: any[]): number {
    return arr.reduce((m, x) => Math.max(m, x.orderNo || 0), 0) + 1;
  }

  blankModel(level: string): any {
    switch (level) {
      case "head":
        return {
          id: 0, name: "", natLong: "", reportType: this.selectedType,
          year: this.years.length ? this.years[0].id : 1, carType: "", isModified: false, deleted: false,
        };
      case "maingroup":
        return {
          id: 0, reportHead: this.selectedHeadId, sign: "+", code: "", name: "", natLong: "",
          emptyInRep: false, itemVisible: false, orderNo: this.nextOrder(this.mainGroups),
          isCode: false, isBold: false, isItalic: false, ejectAfter: false, deleted: false,
        };
      case "group":
        return {
          id: 0, reportMainGroup: this.selMainGroupId, sign: "+", code: "", name: "", natLong: "",
          emptyInRep: false, itemVisible: false, ejectAfter: false, orderNo: this.nextOrder(this.groups),
          isCode: false, isBold: false, isItalic: false, deleted: false,
        };
      case "row":
        return {
          id: 0, reportGroup: this.selGroupId, sign: "+", code: "", name: "", natLong: "",
          emptyInRep: false, orderNo: this.nextOrder(this.rows),
          isCode: false, isBold: false, isItalic: false, ejectAfter: false, deleted: false,
        };
      case "rowitem":
        return {
          id: 0, reportRow: this.selRowId, sign: false, isBalance: false, accSign: false, accNo: "",
          dimcode1: "", dimcode2: "", dimcode3: "", dimcode4: "", dimcode5: "", dimcode6: "", dimcode7: "", dimcode8: "", dimcode9: "",
          rowType: (this.rowTypes.find((rt: any) => rt.default === "+") || this.rowTypes[0] || { id: 1 }).id,
          total: null, isBalanceSign: false,
          year: this.years.length ? this.years[0].id : 1, isCashflow: false, isAccno: false, name: "", deleted: false,
        };
      case "mainitem":
        return {
          id: 0, head: this.selMainGroupId, repMainGroup: 1, repGroup: 1, repRow: 1, sign: "+",
        };
      default:
        return {};
    }
  }

  persist(level: string, model: any): Promise<number> {
    switch (level) {
      // A fej-lista DTO-ja (RiportHeadDTO) a ReportType-ot string mezővel árnyékolja és null-lal
      // adja vissza → a listából visszamentett fej null-t küldene a long ReportType-ra, amitől a
      // backend bind elhasal (NullReferenceException a SaveRiportHead handlerben). Mentés előtt
      // ezért mindig a kiválasztott riport típussal pótoljuk (a fej-lista amúgy is erre szűrt).
      case "head": return api.SaveRiportHead({ ...model, reportType: this.selectedType });
      case "maingroup": return api.SaveRiportMainGroup(model);
      case "group": return api.SaveRiportGroup(model);
      case "row": return api.SaveRiportRow(model);
      case "rowitem": return api.SaveRiportRowItem(model);
      case "mainitem": return api.SaveRiportMainItem(model);
      default: return Promise.resolve(0);
    }
  }

  async reload(level: string): Promise<void> {
    switch (level) {
      case "head": await this.loadHeads(); break;
      case "maingroup": await this.loadMainGroups(); break;
      case "group": await this.loadGroups(); break;
      case "row": await this.loadRows(); break;
      case "rowitem": await this.loadRowItems(); break;
      case "mainitem": await this.loadMainItems(); break;
    }
  }

  listFor(level: string): any[] {
    switch (level) {
      case "maingroup": return this.mainGroups;
      case "group": return this.groups;
      case "row": return this.rows;
      default: return [];
    }
  }

  // Táblánkénti fel/le gomb: a KIJELÖLT sort mozgatja (a sel id alapján). Nincs kijelölés → no-op.
  // CSAK a kliensoldali piszkozat-sorrendet állítja (OrderNo csere + újrarendezés) és dirty-re jelöli
  // a szintet – a DB-be nem ír. A commit a saveOrder, az elvetés a cancelOrder.
  moveSelected(level: string, dir: number): void {
    const id =
      level === "maingroup" ? this.selMainGroupId :
      level === "group" ? this.selGroupId :
      level === "row" ? this.selRowId : null;
    if (id == null) return;
    const arr = this.listFor(level);
    const idx = arr.findIndex((x: any) => x.id === id);
    const j = idx + dir;
    if (idx < 0 || j < 0 || j >= arr.length) return;
    // A kijelölt és a szomszéd OrderNo-jának cseréje, majd újrarendezés OrderNo szerint (ezt jeleníti meg a grid).
    const tmp = arr[idx].orderNo;
    arr[idx].orderNo = arr[j].orderNo;
    arr[j].orderNo = tmp;
    arr.sort((a: any, b: any) => (a.orderNo || 0) - (b.orderNo || 0));
    this.orderDirty[level as "maingroup" | "group" | "row"] = true;
  }

  // A piszkozat-sorrend commitolása egy szintre: egyetlen atomi (tranzakcionális) backend hívás, majd újratöltés.
  async saveOrder(level: string): Promise<void> {
    const arr = this.listFor(level);
    this.busy = true;
    try {
      await api.ReorderRiportNodes(level, arr.map((x: any) => ({ id: x.id, orderNo: x.orderNo })));
      this.orderDirty[level as "maingroup" | "group" | "row"] = false;
      await this.reload(level);
      eventBus.$emit("success", "A sorrend mentése sikeres!");
    } catch (e) {
      eventBus.$emit("error", "A sorrend mentése nem sikerült!");
    } finally {
      this.busy = false;
    }
  }

  // A piszkozat elvetése: visszatölti a DB szerinti eredeti sorrendet (a reload nullázza az orderDirty-t is).
  async cancelOrder(level: string): Promise<void> {
    this.orderDirty[level as "maingroup" | "group" | "row"] = false;
    await this.reload(level);
  }

  // Logikai törlés (Deleted flag) – fej/főcsoport/csoport/sor/sortétel.
  async deleteNode(level: string, item: any): Promise<void> {
    this.busy = true;
    try {
      const model = { ...item, deleted: true };
      await this.persist(level, model);
      eventBus.$emit("success", "Törölve!");
      if (level === "head") {
        this.selectedHeadId = null;
      }
      await this.reload(level);
    } catch (e) {
      eventBus.$emit("error", "A törlés nem sikerült!");
    } finally {
      this.busy = false;
    }
  }

  // Az "elem" törlése FIZIKAI (a táblán nincs live_dead).
  async deleteMainItem(item: any): Promise<void> {
    this.busy = true;
    try {
      await api.DeleteRiportMainItem(item.id);
      eventBus.$emit("success", "Törölve!");
      await this.loadMainItems();
    } catch (e) {
      eventBus.$emit("error", "A törlés nem sikerült!");
    } finally {
      this.busy = false;
    }
  }
}

const store = new RiportStore();
export default store;
